import './hud.css';
import { BALL_COLOR, DEFAULT_THEME, THEMES, type BallId, type ThemeId, type VariantId } from '../config';
import { ENV_IDS, DEFAULT_ENV, type EnvId } from '../render/Environments';
import { VARIANTS, pointsRemaining, inFinalShotStage } from '../rules';
import type { MatchState } from '../rules';
import type { CameraMode } from '../camera/CameraRig';
import type { AimOverlap } from '../render/AimVisuals';
import { t, tText, onLang, setLang, getLang, type Lang } from './i18n';
import { AVATAR_ENABLED, paintAvatar, type AvatarConfig } from '../avatar';
import { tierLabel } from './tier';
import { tiltFromTrack } from '../app/touchControls';
import type { HeadToHead, PlayerStats } from '../net/protocol';

export type UiMode = 'solo' | 'ai' | 'online';

/** Final-stage ("마지막 3쿠션") qualifying caroms + bank shots a player still needs. */
function finalRemaining(
  rule: MatchState['config']['rule'],
  p: MatchState['players'][number]
): { caroms: number; banks: number } {
  const needCaroms = Math.max(1, rule.finalShotCount ?? 1);
  const needBanks = rule.finalBankCount ?? 0;
  // needCaroms is a PLAIN-shot quota separate from needBanks (see match.ts) — the
  // total final shots still needed is against their sum, not needCaroms alone.
  const caromsLeft = Math.max(0, needCaroms + needBanks - p.finalCaroms);
  const banks = Math.max(0, needBanks - p.finalBanks);
  return { caroms: Math.max(caromsLeft, banks), banks };
}

/** Korean handicap presets (points to clear; each carom knocks off 10). */
export const HANDICAP_OPTIONS = [30, 50, 80, 100, 150, 200, 250, 300, 400, 500];

/** Full-scale of the touch tilt dial, in degrees. Mirrors MAX_ELEVATION
 *  (1.75 rad ≈ 100°) in App.ts — past vertical, so a full massé is reachable. */
const MAX_TILT_DEG = 100;

/** Touch camera toggle: tapping cycles through these in order. Orbit ("자유",
 *  free view) is left out on touch — a free-drag orbit camera fights the same
 *  finger drag that aims the cue, so it's desktop-only (reachable there via
 *  1/2/3 + Tab). */
const CAM_CYCLE: CameraMode[] = ['aim', 'top'];

/** Icon shown on the touch camera-toggle button for each mode it can switch to. */
const CAM_EMOJI: Record<CameraMode, string> = { aim: '🎯', top: '🔝', orbit: '🧭', action: '🎬', break: '💥' };

/** Icon shown on the touch chalk button. */
const CHALK_EMOJI = '🧊';

/** Icon shown on the touch aim-boost (hold for 2x aim speed) button. */
const AIM_BOOST_EMOJI = '⚡';

/** Canvas resolution (and on-screen size) of the touch spin pad's magnified
 *  centre-screen mirror — big enough to place spin precisely by eye. */
const SPIN_MAG_SIZE = 220;

export interface HudCallbacks {
  onSpinChange(a: number, b: number): void;
  /** Touch aim pad dragged (see #hud-aim-pad): horizontal delta rotates the
   *  aim, vertical delta adjusts cue elevation — both in px. */
  onAimDrag(dx: number, dy: number): void;
  onCamera(mode: CameraMode): void;
  onVariant(id: VariantId): void;
  onHandicap(value: number): void;
  onTheme(id: ThemeId): void;
  onEnv(id: EnvId): void;
  onMode(mode: UiMode): void;
  onMainMenu(): void;
  onAssess(): void;
  onReplay(): void;
  onScrub(frac: number): void;
  onNewGame(): void;
  /** End-of-game banner: open the shot-review panel (stay in the room). */
  onReviewShots(): void;
  /** Practice mode: reload the practised shot's layout to try it again (the old 'r' key). */
  onRePractice(): void;
  onToggleGuide(): void;
  onEditAvatar(): void;
  /** Touch stroke pad: dragging loads power (0..1) as the cue is pulled back. */
  onStrokeDraw(power01: number): void;
  /** Touch stroke pad released: fire the shot at `power01` when `fire` (enough draw). */
  onStrokeRelease(fire: boolean, power01: number): void;
  /** Re-chalk the tip (tap the chalk meter; the touch analogue of the C key). */
  onChalk(): void;
  /** Touch aim-boost button held down/released: doubles aim-drag gain while active. */
  onAimBoost(active: boolean): void;
  /** Touch tilt dial moved: set the cue elevation ABSOLUTELY, in degrees. */
  onTilt(deg: number): void;
  /** The mid-game prompt banner (e.g. "press any key or click") was clicked. */
  onBannerClick(): void;
}

export interface HudInfo {
  message: string;
  power: number; // 0..1
  aiThinking: boolean;
  canShoot: boolean;
  canReplay: boolean;
  canScrub: boolean;
  mode: UiMode;
  guideOn: boolean;
  cameraMode: CameraMode;
  variant: VariantId;
  /** Optional centred banner (e.g. the break "press any key" prompt). */
  banner?: string | null;
  /** A game is in progress (active play) → hide the reopen-menu button. */
  inGame?: boolean;
  /** Watching a match → show the dedicated "exit spectating" button. */
  spectating?: boolean;
  /** Per-seat all-time stats (id order = player order), shown under each card. */
  stats?: (PlayerStats | null)[];
  /** My head-to-head record against each opponent, keyed by their account id. */
  h2h?: Record<string, HeadToHead | null>;
  /** Assessment session in progress: hide the normal scoreboard. */
  assessing?: boolean;
  /** 당구 교실 lesson in progress: hide the normal scoreboard (the lesson card
   *  takes over its top-left corner instead — see TutorialPanel). */
  tutorial?: boolean;
  /** Assessment finished: HTML for the result banner (level + rank). */
  assessResult?: string | null;
  /** A finished game has recorded shots → show the banner's "Review shots" button. */
  canReview?: boolean;
  /** Practising a recorded shot → show the "re-practice" (reload layout) button. */
  showRePractice?: boolean;
  /** Mid-attempt at the weekly mission → the sub-line shows live progress, not the handicap target. */
  daily?: {
    caroms: number; target: number; strokes: number;
    ruleType: 'caroms' | 'banks' | 'timedScore' | 'timedSpeed'; banks: number; minBanks: number;
    /** 'timedScore' only: seconds left on the countdown. */
    remainingSec?: number;
    /** 'timedSpeed' only: seconds elapsed so far. */
    elapsedSec?: number;
  } | null;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, html?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (html != null) e.innerHTML = html;
  return e;
}

/** Renders a button as an emoji icon with a small caption underneath, instead
 *  of a plain text label — used by the touch control row (camera/chalk),
 *  where an icon reads faster than Korean/English text at that size. */
function setIconLabel(btn: HTMLElement, icon: string, label: string): void {
  btn.innerHTML = `<span class="btn-icon">${icon}</span><span class="btn-label">${label}</span>`;
}

export class HUD {
  private cards: HTMLDivElement[] = [];
  // Purchased cue-ball colours, keyed by ball id — set once per match alongside
  // the 3D BallView override (see App.ts rebuildBalls / startOnlineMatch).
  private colorOverride: Partial<Record<BallId, number>> = {};
  private scorePanel = el('div', 'hud-panel');
  private sub = el('div', 'sub');
  private message = el('div', 'message');
  private dailyBadge = el('div', 'daily-badge');
  /** Caroms/strokes as of the last render — lets a fresh point pop the just-lit
   *  pip, and a scoreless stroke shake the badge, instead of just repainting. */
  private lastDailyCaroms: number | null = null;
  private lastDailyStrokes: number | null = null;
  private replayBtn = el('button', 'replay-btn');
  private scrubRange = el('input');
  private powerBar = el('div', 'power-bar');
  private powerFill = el('div', 'power-fill');
  // touch-only "pull back & release to shoot" pad (shown on touch devices)
  private strokePad = el('div', 'stroke-pad touch-only');
  private strokeFill = el('div', 'stroke-fill');
  private strokeActive = false; // a pull-back is in progress
  private strokeStartY = 0; // clientY where the pull began
  private strokeReady = false; // your turn + can shoot (pad accepts input)
  private chalkBar = el('div', 'chalk-bar');
  private chalkFill = el('div', 'chalk-fill');
  // touch-only: the chalk bar is tappable but reads as a status meter, not a
  // control — a proper button makes it obvious there's an action there.
  private chalkBtn = el('button', 'chalk-btn touch-only');
  private tiltReadout = el('div', 'label tilt-readout');
  // touch-only vertical tilt dial: drag up to raise the cue (massé), down to level.
  // Elevation is otherwise unreachable on touch — the canvas drag only rotates aim.
  private tiltDial = el('div', 'tilt-dial touch-only');
  private tiltThumb = el('div', 'tilt-thumb');
  private tiltFill = el('div', 'tilt-fill');
  private spinPad = el('canvas');
  // touch-only: bigger mirror of the spin pad, shown centre-screen while dragging
  private spinPadMag = el('canvas', 'touch-only');
  // touch-only: larger pad under the spin pad — a dedicated horizontal drag
  // target for aim, separate from dragging the table itself.
  private aimPad = el('div', 'aim-pad touch-only');
  private modeSel = el('select');
  private themeSel = el('select');
  private envSel = el('select');
  private handicapSel = el('select');
  // in-game quick-settings (single play): change level + background mid-game
  private gearFab = el('button');
  private quickPanel = el('div', 'hud-panel interactive');
  private quickHandicapSel = el('select');
  private quickEnvSel = el('select');
  private onlineBtn = el('button');
  // touch-only: cycles aim → top-down → orbit on tap, one button standing in
  // for the desktop 1/2/3 + Tab shortcuts (see CAM_CYCLE below).
  private camToggleBtn = el('button', 'cam-toggle touch-only');
  // touch-only: hold to double the aim-drag gain — a finger's drag travel is
  // short, so a big swing across the table otherwise takes several strokes.
  private aimBoostBtn = el('button', 'aim-boost-btn touch-only');
  private cameraModeState: CameraMode = 'aim';
  private banner = el('div', 'hud-panel', '');
  private bannerText = el('div', 'banner-text');
  private bannerBtns = el('div', 'banner-btns');
  private bannerMenuBtn = el('button', 'banner-menu-btn');
  private bannerReviewBtn = el('button', 'banner-review-btn'); // open the shot-review panel
  private bannerDismissed = false; // end banner hidden (e.g. after opening the review)
  private menuFab = el('button');
  private specExitBtn = el('button', 'interactive'); // top-right exit (spectating or in-game)
  private rePracticeBtn = el('button', 'interactive'); // practice mode: reload the shot layout ('r')
  private exitSpectating = false; // drives the exit button's label (관전 종료 vs 나가기)
  private hudAvatar = el('button');
  private guideBtn = el('button');
  private guideOnState = true; // tracked so the button label can re-translate
  private tiltDeg = 0; // current cue tilt (degrees) shown in the controls pane
  private spin = { a: 0, b: 0 };
  private aimOverlap: AimOverlap | null = null; // current aim's cue/object ball overlap
  private redrawSpin: () => void = () => {};

  constructor(root: HTMLElement, private cb: HudCallbacks) {
    root.appendChild(this.buildScore());
    root.appendChild(this.buildStatus());
    root.appendChild(this.buildMenu());
    const controlsPanel = this.buildControls(root);
    root.appendChild(controlsPanel);
    root.appendChild(this.buildHint());
    this.banner.id = 'hud-banner';
    this.banner.classList.add('interactive');
    this.bannerBtns.append(this.bannerMenuBtn, this.bannerReviewBtn);
    this.banner.append(this.bannerText, this.bannerBtns);
    tText(this.bannerMenuBtn, 'prompt.toMenu');
    this.bannerMenuBtn.onclick = () => this.cb.onMainMenu();
    tText(this.bannerReviewBtn, 'btn.reviewShots');
    this.bannerReviewBtn.onclick = () => this.cb.onReviewShots();
    // mid-game prompts (e.g. break's "press any key or click") have no buttons of
    // their own — clicking the banner text should act like the click it's asking for,
    // not silently swallow it (the banner sits above the canvas and blocks the click).
    this.bannerText.onclick = () => this.cb.onBannerClick();
    root.appendChild(this.banner);

    // The full top-right menu is hidden for now; a single floating button remains
    // to reopen the start menu — but it's hidden once a game is in progress.
    this.menuFab.classList.add('interactive');
    this.menuFab.id = 'hud-menu-fab';
    this.menuFab.textContent = '☰';
    this.menuFab.title = t('menu.mainMenu');
    onLang(() => (this.menuFab.title = t('menu.mainMenu')));
    this.menuFab.onclick = () => this.cb.onMainMenu();
    this.menuFab.style.display = 'none'; // top-right menu button disabled
    // root.appendChild(this.menuFab); // disabled: no top-right corner menu

    // top-right "exit to main menu" button, since the general menu button is
    // disabled. Shown while spectating a match OR while in your own active game
    // (leaving an online match forfeits — App warns before it does).
    this.specExitBtn.id = 'hud-spec-exit';
    this.specExitBtn.textContent = `← ${t('hud.exitSpectate')}`;
    onLang(() => (this.specExitBtn.textContent = `← ${t(this.exitSpectating ? 'hud.exitSpectate' : 'hud.exitGame')}`));
    this.specExitBtn.onclick = () => this.cb.onMainMenu();
    this.specExitBtn.style.display = 'none';
    root.appendChild(this.specExitBtn);

    // re-practice: reload the practised shot's layout to try it again, replacing
    // the old 'r' keypress (which is undiscoverable, esp. on touch). Shown only
    // while practising a recorded shot. Desktop sits it below the top-right exit
    // button; touch instead prepends it INTO the shot-controls panel (the
    // reachable bottom-right corner) rather than the top-right corner, which is
    // a long one-handed reach — see hud.css for the position override.
    this.rePracticeBtn.id = 'hud-repractice';
    this.rePracticeBtn.textContent = `↺ ${t('btn.rePractice')}`;
    onLang(() => (this.rePracticeBtn.textContent = `↺ ${t('btn.rePractice')}`));
    this.rePracticeBtn.onclick = () => this.cb.onRePractice();
    this.rePracticeBtn.style.display = 'none';
    if (document.body.classList.contains('touch')) {
      controlsPanel.prepend(this.rePracticeBtn);
    } else {
      root.appendChild(this.rePracticeBtn);
    }

    // profile avatar FAB (bottom-left): click to open the avatar editor mid-game
    if (AVATAR_ENABLED) {
      this.hudAvatar.classList.add('hud-avatar', 'interactive');
      this.hudAvatar.id = 'hud-avatar-fab';
      this.hudAvatar.title = t('avatar.edit');
      onLang(() => (this.hudAvatar.title = t('avatar.edit')));
      this.hudAvatar.onclick = () => this.cb.onEditAvatar();
      root.appendChild(this.hudAvatar);
    }

    root.appendChild(this.buildQuick());
  }

  /** Paint the local player's profile avatar into the HUD FAB. */
  setAvatar(cfg: AvatarConfig): void {
    paintAvatar(this.hudAvatar, cfg);
  }

  /** Compact in-game settings for single play: change level + background mid-game.
   *  Wired to the same persisted handlers as the (hidden) full menu. Toggled by a
   *  ⚙ gear that only appears while playing solo / vs AI (shown from update()). */
  private buildQuick(): HTMLElement {
    HANDICAP_OPTIONS.forEach((v) => {
      const o = el('option');
      o.value = String(v);
      o.textContent = String(v);
      this.quickHandicapSel.appendChild(o);
    });
    this.quickHandicapSel.onchange = () => this.cb.onHandicap(Number(this.quickHandicapSel.value));
    const hcLabel = el('label');
    tText(hcLabel.appendChild(el('span')), 'menu.handicap');
    hcLabel.appendChild(this.quickHandicapSel);

    ENV_IDS.forEach((id) => {
      const o = el('option');
      o.value = id;
      tText(o, 'env.' + id);
      this.quickEnvSel.appendChild(o);
    });
    this.quickEnvSel.onchange = () => this.cb.onEnv(this.quickEnvSel.value as EnvId);
    const envLabel = el('label');
    tText(envLabel.appendChild(el('span')), 'menu.env');
    envLabel.appendChild(this.quickEnvSel);

    this.quickPanel.id = 'hud-quick';
    this.quickPanel.style.display = 'none';
    this.quickPanel.append(hcLabel, envLabel);

    this.gearFab.classList.add('interactive');
    this.gearFab.id = 'hud-gear-fab';
    this.gearFab.textContent = '⚙';
    this.gearFab.title = t('menu.settings');
    onLang(() => (this.gearFab.title = t('menu.settings')));
    this.gearFab.style.display = 'none';
    this.gearFab.onclick = () => {
      const open = this.quickPanel.style.display === 'none';
      this.quickPanel.style.display = open ? '' : 'none';
    };

    const wrap = el('div');
    wrap.append(this.gearFab, this.quickPanel);
    return wrap;
  }

  private buildScore(): HTMLElement {
    const panel = this.scorePanel;
    panel.id = 'hud-score';
    for (let i = 0; i < 4; i++) {
      const card = el('div', 'player-card');
      // the score is a countdown of points still to clear, so it carries a unit
      // ("남음" / "to go") — a bare number reads like a running total
      card.innerHTML = `
        <div class="turn-timer" style="display:none"></div>
        <div class="name"><span class="swatch"></span><span class="pname"></span></div>
        <div class="score-row"><div class="score">0</div><div class="score-unit"></div></div>
        <div class="final-badge"></div>
        <div class="card-stats"></div>`;
      this.cards.push(card);
      panel.appendChild(card);
    }
    return panel;
  }

  /** Pop a floating emote bubble (glyph + name) by the sender's player card; a
   *  spectator (seat < 0) shows centred near the top. Auto-removes after the anim. */
  showEmote(seat: number, glyph: string, name: string): void {
    const bubble = el('div', 'emote-bubble');
    bubble.innerHTML = `<div class="eb-inner"><span class="eb-glyph">${glyph}</span><span class="eb-name">${name}</span></div>`;
    const card = seat >= 0 ? this.cards[seat] : null;
    if (card && card.style.display !== 'none') {
      const r = card.getBoundingClientRect();
      bubble.style.left = `${r.left + r.width / 2}px`;
      bubble.style.top = `${r.bottom + 6}px`;
    } else {
      bubble.style.left = '50%';
      bubble.style.top = '92px';
    }
    bubble.style.transform = 'translateX(-50%)';
    document.body.appendChild(bubble);
    setTimeout(() => bubble.remove(), 2300);
  }

  /** Fill a player-card's all-time stats strip (다마 / Elo / win-rate; no in-game rank). */
  private renderCardStats(card: HTMLElement, st: PlayerStats | null | undefined, h2h?: HeadToHead | null): void {
    const box = card.querySelector('.card-stats') as HTMLElement;
    if (!st) {
      box.style.display = 'none';
      box.replaceChildren();
      return;
    }
    const games = st.wins + st.losses;
    const wr = games ? Math.round((st.wins / games) * 100) : 0;
    const item = (k: string, v: string) => `<span class="cs-i"><span class="cs-k">${k}</span><span class="cs-v">${v}</span></span>`;
    // rank (순위) is intentionally not shown during a game
    box.innerHTML =
      item(t('hud.level'), tierLabel(st.tier)) +
      item('Elo', String(st.rating)) +
      item(t('hud.winrate'), `${wr}% (${st.wins}-${st.losses})`) +
      item(t('hud.coins'), String(st.coins)) +
      // The rivalry beats the ladder for a 1v1 game: a handicap match is ~50/50
      // by design, so "3승 4패 vs them" is the story the Elo can't tell.
      (h2h && h2h.wins + h2h.losses > 0
        ? `<span class="cs-i cs-h2h"><span class="cs-k">vs</span><span class="cs-v">${h2h.wins}승 ${h2h.losses}패</span></span>`
        : '');
    box.style.display = '';
  }

  private buildStatus(): HTMLElement {
    const panel = el('div', 'hud-panel');
    panel.id = 'hud-status';
    panel.append(this.dailyBadge, this.sub, this.message);
    return panel;
  }

  /** Render the weekly-mission tracker: a pip per carom needed, lit as they land,
   *  plus a stroke counter — replaces the plain "N/target" sub-line with something
   *  that reads as a game HUD, not a status log. */
  private renderDailyBadge(d: NonNullable<HudInfo['daily']>): void {
    const justScored = this.lastDailyCaroms != null && d.caroms > this.lastDailyCaroms;
    const justMissed = !justScored && this.lastDailyStrokes != null && d.strokes > this.lastDailyStrokes;
    this.lastDailyCaroms = d.caroms;
    this.lastDailyStrokes = d.strokes;

    const timedScore = d.ruleType === 'timedScore';
    const pips = timedScore ? '' : Array.from({ length: d.target }, (_, i) => {
      const lit = i < d.caroms;
      const pop = lit && i === d.caroms - 1 && justScored;
      return `<span class="daily-badge__pip${lit ? ' lit' : ''}${pop ? ' pop' : ''}"></span>`;
    }).join('');
    const bankChip = d.ruleType === 'banks'
      ? `<span class="daily-badge__bank">${t('daily.badgeBanks', { b: d.banks, m: d.minBanks })}</span>`
      : '';
    const scoreChip = timedScore ? `<span class="daily-badge__score">${t('daily.badgeScore', { n: d.caroms })}</span>` : '';
    const timerChip = timedScore && d.remainingSec != null
      ? `<span class="daily-badge__timer">${t('daily.badgeTimeLeft', { n: d.remainingSec })}</span>`
      : d.ruleType === 'timedSpeed' && d.elapsedSec != null
        ? `<span class="daily-badge__timer">${t('daily.badgeElapsed', { n: d.elapsedSec })}</span>`
        : '';
    this.dailyBadge.innerHTML = `
      <span class="daily-badge__icon">🔥</span>
      <span class="daily-badge__pips">${pips}</span>
      ${scoreChip}
      ${bankChip}
      ${timerChip}
      <span class="daily-badge__strokes">${t('daily.badgeStrokes', { n: d.strokes })}</span>`;
    this.dailyBadge.classList.toggle('flash', justScored);
    this.dailyBadge.classList.toggle('shake', justMissed);
    if (justScored || justMissed) {
      setTimeout(() => this.dailyBadge.classList.remove('flash', 'shake'), 500);
    }
  }

  private buildMenu(): HTMLElement {
    const panel = el('div', 'hud-panel interactive');
    panel.id = 'hud-menu';

    const variantSel = el('select');
    (Object.keys(VARIANTS) as VariantId[]).forEach((id) => {
      const o = el('option');
      o.value = id;
      tText(o, 'variant.' + id);
      variantSel.appendChild(o);
    });
    variantSel.value = 'straight-rail';
    variantSel.onchange = () => this.cb.onVariant(variantSel.value as VariantId);
    const variantLabel = el('label');
    tText(variantLabel.appendChild(el('span')), 'menu.game');
    variantLabel.appendChild(variantSel);

    // language switch
    const langSel = el('select');
    (
      [
        ['en', 'English'],
        ['ko', '한국어'],
      ] as [Lang, string][]
    ).forEach(([v, label]) => {
      const o = el('option');
      o.value = v;
      o.textContent = label;
      langSel.appendChild(o);
    });
    langSel.value = getLang();
    langSel.onchange = () => setLang(langSel.value as Lang);
    const langLabel = el('label');
    tText(langLabel.appendChild(el('span')), 'menu.language');
    langLabel.appendChild(langSel);

    const handicapSel = this.handicapSel;
    HANDICAP_OPTIONS.forEach((v) => {
      const o = el('option');
      o.value = String(v);
      o.textContent = String(v);
      handicapSel.appendChild(o);
    });
    handicapSel.value = '100';
    handicapSel.onchange = () => this.cb.onHandicap(Number(handicapSel.value));
    const handicapLabel = el('label');
    tText(handicapLabel.appendChild(el('span')), 'menu.handicap');
    handicapLabel.appendChild(handicapSel);

    const themeSel = this.themeSel;
    (Object.keys(THEMES) as ThemeId[]).forEach((id) => {
      const o = el('option');
      o.value = id;
      tText(o, 'theme.' + id);
      themeSel.appendChild(o);
    });
    themeSel.value = DEFAULT_THEME;
    themeSel.onchange = () => this.cb.onTheme(themeSel.value as ThemeId);
    const themeLabel = el('label');
    tText(themeLabel.appendChild(el('span')), 'menu.theme');
    themeLabel.appendChild(themeSel);

    // Room backdrop: the procedural billiard hall or a real 360° photo scene.
    const envSel = this.envSel;
    ENV_IDS.forEach((id) => {
      const o = el('option');
      o.value = id;
      tText(o, 'env.' + id);
      envSel.appendChild(o);
    });
    envSel.value = DEFAULT_ENV;
    envSel.onchange = () => this.cb.onEnv(envSel.value as EnvId);
    const envLabel = el('label');
    tText(envLabel.appendChild(el('span')), 'menu.env');
    envLabel.appendChild(envSel);

    // Local play: solo practice or vs AI. Online is its own button (below).
    (['solo', 'ai'] as UiMode[]).forEach((v) => {
      const o = el('option');
      o.value = v;
      tText(o, 'mode.' + v);
      this.modeSel.appendChild(o);
    });
    this.modeSel.value = 'solo';
    this.modeSel.onchange = () => this.cb.onMode(this.modeSel.value as UiMode);
    const modeLabel = el('label');
    tText(modeLabel.appendChild(el('span')), 'menu.mode');
    modeLabel.appendChild(this.modeSel);

    // Online play lives on its own button (👥), separate from the local modes.
    this.onlineBtn.textContent = '👥';
    this.onlineBtn.title = t('mode.online');
    onLang(() => (this.onlineBtn.title = t('mode.online')));
    this.onlineBtn.onclick = () => this.cb.onMode('online');
    modeLabel.appendChild(this.onlineBtn);

    // Skill assessment lives on its own button (📋): a fixed-length solo session
    // that calibrates the player's level / rank from how often they score.
    const assessBtn = el('button');
    assessBtn.textContent = '📋';
    assessBtn.title = t('menu.assess');
    onLang(() => (assessBtn.title = t('menu.assess')));
    assessBtn.onclick = () => this.cb.onAssess();
    modeLabel.appendChild(assessBtn);

    // reopen the start menu (mode + level)
    const menuBtn = el('button');
    menuBtn.textContent = '☰';
    menuBtn.title = t('menu.mainMenu');
    onLang(() => (menuBtn.title = t('menu.mainMenu')));
    menuBtn.onclick = () => this.cb.onMainMenu();
    modeLabel.appendChild(menuBtn);

    onLang(() => (this.guideBtn.textContent = t(this.guideOnState ? 'btn.guideOn' : 'btn.guideOff')));
    this.guideBtn.onclick = () => this.cb.onToggleGuide();

    const newGame = el('button');
    tText(newGame, 'btn.newGame');
    newGame.onclick = () => this.cb.onNewGame();

    panel.append(variantLabel, modeLabel, handicapLabel, themeLabel, envLabel, langLabel, this.guideBtn, newGame);
    return panel;
  }

  private buildControls(root: HTMLElement): HTMLElement {
    const panel = el('div', 'hud-panel interactive');
    panel.id = 'hud-controls';

    // spin pad
    this.spinPad.id = 'spin-pad';
    this.spinPad.width = 96;
    this.spinPad.height = 96;
    this.bindSpinPad();
    const spinLabel = el('div', 'label');
    tText(spinLabel, 'ctrl.spin');
    const aimPadLabel = el('div', 'label');
    tText(aimPadLabel, 'ctrl.aimPad');

    // chalk meter — full when freshly chalked (press C), drains as the tip is used.
    // Also tappable (touch has no keyboard) to re-chalk between shots.
    this.chalkBar.appendChild(this.chalkFill);
    this.setChalk(1);
    this.chalkBar.classList.add('interactive');
    this.chalkBar.style.cursor = 'pointer';
    this.chalkBar.addEventListener('pointerdown', () => this.cb.onChalk());
    setIconLabel(this.chalkBtn, CHALK_EMOJI, t('btn.chalk'));
    onLang(() => setIconLabel(this.chalkBtn, CHALK_EMOJI, t('btn.chalk')));
    this.chalkBtn.onclick = () => this.cb.onChalk();

    // aim-boost — press-and-hold, not a tap: it only matters while dragging,
    // so it releases itself (pointerup/cancel/leave) rather than needing a
    // second tap to turn back off.
    setIconLabel(this.aimBoostBtn, AIM_BOOST_EMOJI, t('btn.aimBoost'));
    onLang(() => setIconLabel(this.aimBoostBtn, AIM_BOOST_EMOJI, t('btn.aimBoost')));
    this.aimBoostBtn.addEventListener('contextmenu', (e) => e.preventDefault());
    const setAimBoost = (active: boolean) => {
      this.aimBoostBtn.classList.toggle('active', active);
      this.cb.onAimBoost(active);
    };
    this.aimBoostBtn.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.aimBoostBtn.setPointerCapture(e.pointerId);
      setAimBoost(true);
    });
    this.aimBoostBtn.addEventListener('pointerup', () => setAimBoost(false));
    this.aimBoostBtn.addEventListener('pointercancel', () => setAimBoost(false));

    // power — read-only bar; power is loaded by the Ctrl pull-back stroke.
    // The caption is hidden on touch (see hud.css) — the fill level already
    // says "power", the word next to it is redundant on a small screen.
    this.powerBar.appendChild(this.powerFill);
    this.setPower(0.55);
    const powerLabel = el('div', 'label power-label');
    tText(powerLabel, 'ctrl.power');

    // touch-only stroke pad: pull down to load power, release to shoot
    const strokeHint = el('div', 'stroke-hint');
    tText(strokeHint, 'ctrl.pullShoot');
    this.strokePad.append(this.strokeFill, strokeHint);
    this.bindStrokePad();

    // cue tilt readout (driven from the aim loop) + the touch dial that sets it.
    // The readout text is hidden on touch (see hud.css) — the dial's thumb
    // position already shows the tilt.
    this.setTilt(0);
    onLang(() => this.setTilt(this.tiltDeg));
    this.tiltDial.append(this.tiltFill, this.tiltThumb);
    this.bindTiltDial();
    // Stroke pad and tilt dial sit side by side: both are tall vertical drags,
    // and stacking them would push the controls off a landscape phone.
    const strokeRow = el('div', 'stroke-row touch-only');
    strokeRow.append(this.strokePad, this.tiltDial);

    // camera toggle — touch-only: desktop reaches these modes with 1/2/3 +
    // Tab, but there's no keyboard on a phone, so aim/top-down/orbit are
    // otherwise unreachable there. One button cycling the 3 modes rather than
    // 3 separate ones — see CAM_CYCLE.
    this.camToggleBtn.onclick = () => {
      const i = CAM_CYCLE.indexOf(this.cameraModeState);
      this.cb.onCamera(CAM_CYCLE[(i + 1) % CAM_CYCLE.length] ?? CAM_CYCLE[0]);
    };
    onLang(() => this.refreshCamToggleLabel());
    // chalk sits next to the camera toggle rather than under its own bar —
    // both are one-tap actions, so they share a row.
    const toggleRow = el('div', 'toggle-row touch-only');
    toggleRow.append(this.camToggleBtn, this.chalkBtn, this.aimBoostBtn);
    // Desktop shows the chalk level as its own thin bar (chalkBar, tappable);
    // touch instead fills the chalk button itself (see setChalk/hud.css), so
    // chalkBar is hidden there — kept in the DOM either way for simplicity.
    const chalkToggleWrap = el('div', 'chalk-toggle');
    chalkToggleWrap.append(this.chalkBar, toggleRow);

    // replay — hidden entirely on touch (see hud.css): solo practice only,
    // and there's no room for it once the camera toggle takes its spot.
    tText(this.replayBtn, 'btn.replay');
    this.replayBtn.onclick = () => this.cb.onReplay();

    // scrub through the last shot's timeline (move the mouse over the table to resume)
    this.scrubRange.id = 'scrub-range';
    this.scrubRange.type = 'range';
    this.scrubRange.min = '0';
    this.scrubRange.max = '100';
    this.scrubRange.value = '100';
    this.scrubRange.oninput = () => this.cb.onScrub(Number(this.scrubRange.value) / 100);
    const scrubLabel = el('div', 'label');
    tText(scrubLabel, 'ctrl.scrub');

    // The scrub control (구간 이동) is intentionally NOT appended — kept built so
    // state updates that reference it stay safe, but hidden from the HUD. The
    // replay button IS shown, but only in solo practice (see update(): info.mode
    // === 'solo') — visibility is toggled there, not here.
    void scrubLabel;
    panel.append(chalkToggleWrap, powerLabel, this.powerBar, this.tiltReadout, strokeRow, this.replayBtn);

    // Touch: the spin control gets its own panel, pinned to the bottom-left
    // corner opposite #hud-controls, so both thumbs have their own target
    // instead of everything cramming into one column. It has to be a sibling
    // of #hud-controls rather than nested inside it — #hud-controls' own
    // backdrop-filter (from .hud-panel) makes it a containing block, which
    // would silently break position:fixed/absolute on anything pinned inside
    // it. Desktop keeps the original, simpler layout: spin renders inline at
    // the top of the one control column, same as always.
    if (document.body.classList.contains('touch')) {
      const spinPanel = el('div', 'hud-panel interactive touch-only');
      spinPanel.id = 'hud-spin';
      this.aimPad.id = 'hud-aim-pad';
      this.bindAimPad();
      spinPanel.append(spinLabel, this.spinPad, aimPadLabel, this.aimPad);
      root.appendChild(spinPanel);
      // Magnified copy shown centre-screen only while dragging (see
      // bindSpinPad) — the pad itself is small on a phone, too small to place
      // spin precisely by eye, so the drag is mirrored bigger where it's easy
      // to see without blocking the shot.
      this.spinPadMag.id = 'spin-pad-mag';
      this.spinPadMag.width = SPIN_MAG_SIZE;
      this.spinPadMag.height = SPIN_MAG_SIZE;
      root.appendChild(this.spinPadMag);
    } else {
      panel.prepend(this.spinPad);
      panel.prepend(spinLabel);
    }
    return panel;
  }

  /**
   * Vertical tilt dial (touch). Maps the track ABSOLUTELY to 0…MAX_TILT_DEG:
   * a dial you can see the position of beats a relative drag, because the cue
   * elevation is otherwise invisible until the shot goes wrong. Dragging the
   * track's top raises the cue toward massé, the bottom returns it to level.
   */
  private bindTiltDial(): void {
    const dial = this.tiltDial;
    let dragging = false;
    const setFrom = (clientY: number): void => {
      const r = dial.getBoundingClientRect();
      const deg = tiltFromTrack(clientY, r.top, r.height, MAX_TILT_DEG);
      if (deg !== null) this.cb.onTilt(deg);
    };
    dial.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      dragging = true;
      dial.setPointerCapture(e.pointerId);
      dial.classList.add('active');
      setFrom(e.clientY);
    });
    dial.addEventListener('pointermove', (e) => {
      if (dragging) setFrom(e.clientY);
    });
    const end = (): void => {
      dragging = false;
      dial.classList.remove('active');
    };
    dial.addEventListener('pointerup', end);
    dial.addEventListener('pointercancel', end);
    // A double-tap on the dial snaps back to a level cue — recovering from an
    // accidental massé otherwise means dragging precisely to the very bottom.
    let lastTap = 0;
    dial.addEventListener('pointerup', () => {
      const now = performance.now();
      if (now - lastTap < 300) this.cb.onTilt(0);
      lastTap = now;
    });
  }

  /** Pull-back-and-release stroke pad (touch). Draw distance → power; release fires. */
  private bindStrokePad(): void {
    const pad = this.strokePad;
    const RANGE = 150; // px of downward pull for full power
    const MIN = 0.06; // minimum draw that counts as a real stroke (else a cancel)
    const drawAt = (y: number) => Math.max(0, Math.min(1, (y - this.strokeStartY) / RANGE));
    pad.addEventListener('pointerdown', (e) => {
      if (!this.strokeReady) return;
      e.preventDefault();
      this.strokeActive = true;
      this.strokeStartY = e.clientY;
      pad.setPointerCapture(e.pointerId);
      pad.classList.add('active');
    });
    pad.addEventListener('pointermove', (e) => {
      if (!this.strokeActive) return;
      const d = drawAt(e.clientY);
      this.strokeFill.style.height = `${d * 100}%`;
      this.cb.onStrokeDraw(d);
    });
    const end = (e: PointerEvent) => {
      if (!this.strokeActive) return;
      this.strokeActive = false;
      pad.classList.remove('active');
      const d = drawAt(e.clientY);
      this.strokeFill.style.height = '0%';
      this.cb.onStrokeRelease(d >= MIN, d);
    };
    pad.addEventListener('pointerup', end);
    pad.addEventListener('pointercancel', end);
  }

  private buildHint(): HTMLElement {
    const panel = el('div', 'hud-panel', '');
    panel.id = 'hud-hint';
    onLang(() => (panel.innerHTML = t('hint')));
    return panel;
  }

  /** Paints the spin dial (cue-ball outline, crosshair, aim-overlap lens, spin
   *  dot) onto any square canvas context — shared by the small pad and its
   *  touch-only magnified mirror, scaled to whatever `size` that canvas is. */
  private paintSpinDial(context: CanvasRenderingContext2D, size: number): void {
    const r = size * (44 / 96);
    const cx = size / 2;
    const cy = size / 2;
    context.clearRect(0, 0, size, size);

    // aim overlap: the object ball sitting over the cue ball, shaded where they
    // overlap, so you can read how full/thin the predicted hit is.
    const ov = this.aimOverlap;
    if (ov) {
      const ox = cx + ov.side * r; // object-ball centre offset (cue-radius units → px)
      const cr = (ov.color >> 16) & 0xff;
      const cg = (ov.color >> 8) & 0xff;
      const cb = ov.color & 0xff;
      context.save();
      context.beginPath();
      context.arc(cx, cy, r, 0, Math.PI * 2);
      context.clip(); // shade only the lens inside the cue ball
      context.fillStyle = `rgba(${cr},${cg},${cb},0.45)`;
      context.beginPath();
      context.arc(ox, cy, r, 0, Math.PI * 2);
      context.fill();
      context.restore();
      context.strokeStyle = `rgba(${cr},${cg},${cb},0.9)`;
      context.lineWidth = 1.5;
      context.beginPath();
      context.arc(ox, cy, r, 0, Math.PI * 2);
      context.stroke();
      context.lineWidth = 1;
      context.fillStyle = 'rgba(255,255,255,0.9)';
      context.font = `600 ${size * (11 / 96)}px system-ui, sans-serif`;
      context.textAlign = 'center';
      context.fillText(`${Math.round(ov.fraction * 100)}%`, cx, size * (11 / 96));
    }

    // cue-ball outline (your ball)
    context.strokeStyle = 'rgba(255,255,255,0.28)';
    context.beginPath();
    context.arc(cx, cy, r, 0, Math.PI * 2);
    context.stroke();

    // spin crosshair
    context.strokeStyle = 'rgba(0,0,0,0.3)';
    context.beginPath();
    context.moveTo(cx - r, cy);
    context.lineTo(cx + r, cy);
    context.moveTo(cx, cy - r);
    context.lineTo(cx, cy + r);
    context.stroke();

    // spin contact dot
    context.fillStyle = '#c81f1f';
    context.beginPath();
    context.arc(cx + this.spin.a * r, cy - this.spin.b * r, size * (6 / 96), 0, Math.PI * 2);
    context.fill();
  }

  /** Touch aim pad: a dedicated drag target for aiming, under the spin pad —
   *  dragging left/right here rotates the aim the same as dragging the table
   *  itself, but doesn't compete with the table for the finger that's also
   *  trying to orbit/zoom the camera. Dragging up/down also adjusts cue
   *  elevation, same as it would on the tilt dial — a second way to reach the
   *  same control, not a replacement for it. */
  private bindAimPad(): void {
    let lastX = 0;
    let lastY = 0;
    this.aimPad.addEventListener('pointerdown', (e) => {
      this.aimPad.setPointerCapture(e.pointerId);
      lastX = e.clientX;
      lastY = e.clientY;
    });
    this.aimPad.addEventListener('pointermove', (e) => {
      if (!this.aimPad.hasPointerCapture(e.pointerId)) return;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      if (dx !== 0 || dy !== 0) this.cb.onAimDrag(dx, dy);
    });
  }

  private bindSpinPad(): void {
    const ctx = this.spinPad.getContext('2d')!;
    const magCtx = this.spinPadMag.getContext('2d');
    const isTouch = () => document.body.classList.contains('touch');
    const draw = () => {
      this.paintSpinDial(ctx, 96);
      if (magCtx && this.spinPadMag.classList.contains('show')) this.paintSpinDial(magCtx, SPIN_MAG_SIZE);
    };
    const clampSpin = (a: number, b: number): { a: number; b: number } => {
      const mag = Math.hypot(a, b);
      return mag > 1 ? { a: a / mag, b: b / mag } : { a, b };
    };
    const apply = (a: number, b: number) => {
      this.spin = clampSpin(a, b);
      this.cb.onSpinChange(this.spin.a, this.spin.b);
      draw();
    };
    // Mouse/desktop: tap-to-position, mapped straight off the pad's own rect.
    const setFromRect = (ev: PointerEvent) => {
      const rect = this.spinPad.getBoundingClientRect();
      const r = rect.width / 2 - rect.width * (4 / 96);
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      apply((ev.clientX - cx) / r, -(ev.clientY - cy) / r);
    };
    // Touch: the pad is small on a phone (see hud.css), too small to place
    // spin precisely by tapping a point on it. Instead the first touch shows
    // a magnified mirror centre-screen and drags adjust spin RELATIVE to that
    // touch, scaled to the magnifier's bigger radius — the same finger travel
    // now reads as a finer spin change, wherever on screen the drag wanders
    // (pointer capture keeps events coming even once the finger leaves the pad).
    const magDragRadius = SPIN_MAG_SIZE / 2 - SPIN_MAG_SIZE * (4 / 96);
    let anchorX = 0;
    let anchorY = 0;
    let anchorSpin = { a: 0, b: 0 };
    let dragging = false;
    this.spinPad.addEventListener('pointerdown', (e) => {
      dragging = true;
      try {
        this.spinPad.setPointerCapture(e.pointerId);
      } catch {
        // ignore — some browsers refuse capture for a pointer they don't
        // consider active; the pad still tracks moves that stay over it.
      }
      if (isTouch()) {
        anchorX = e.clientX;
        anchorY = e.clientY;
        anchorSpin = this.spin;
        this.spinPadMag.classList.add('show');
        draw();
      } else {
        setFromRect(e);
      }
    });
    this.spinPad.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      if (isTouch()) {
        apply(anchorSpin.a + (e.clientX - anchorX) / magDragRadius, anchorSpin.b - (e.clientY - anchorY) / magDragRadius);
      } else {
        setFromRect(e);
      }
    });
    const endDrag = () => {
      dragging = false;
      this.spinPadMag.classList.remove('show');
    };
    this.spinPad.addEventListener('pointerup', endDrag);
    this.spinPad.addEventListener('pointercancel', endDrag);
    this.redrawSpin = draw;
    draw();
  }

  /** Set the spin externally (e.g. from Shift + mouse) and redraw the pad. */
  setSpin(a: number, b: number): void {
    this.spin = { a, b };
    this.redrawSpin();
  }

  /** Show (or clear) the predicted cue/object-ball overlap on the spin pad.
   * No-ops when unchanged so the per-frame aim loop doesn't redraw needlessly. */
  setAimOverlap(o: AimOverlap | null): void {
    const prev = this.aimOverlap;
    const same =
      (!prev && !o) ||
      (!!prev && !!o && prev.fraction === o.fraction && prev.side === o.side && prev.color === o.color);
    if (same) return;
    this.aimOverlap = o;
    this.redrawSpin();
  }

  /** Online PvP shot clock: shown on the timed player's own card (seat = their
   *  own; the opponent's client never learns the exact remaining time, so it
   *  shows nothing for them). `null` hides it. `urgentAtMs` mirrors App.ts's
   *  TURN_TICK_START_MS so the two never drift apart if that constant changes. */
  setTurnTimer(seat: number, remainingMs: number | null, urgentAtMs = 10_000): void {
    const card = this.cards[seat];
    const el = card?.querySelector('.turn-timer') as HTMLElement | null;
    if (!el) return;
    if (remainingMs == null) {
      el.style.display = 'none';
      el.classList.remove('urgent');
      return;
    }
    el.style.display = '';
    el.textContent = String(Math.max(0, Math.ceil(remainingMs / 1000)));
    el.classList.toggle('urgent', remainingMs <= urgentAtMs);
  }

  /** Purchased cue-ball colours for the current match (see App.ts startOnlineMatch). */
  setColorOverride(override: Partial<Record<BallId, number>>): void {
    this.colorOverride = override;
  }

  update(state: MatchState, info: HudInfo): void {
    const solo = state.config.solo === true;
    this.menuFab.style.display = 'none'; // top-right corner menu disabled
    // exit button: while spectating, or while in your own active game (so you can
    // always leave mid-game). Spectating takes label priority.
    const showExit = !!info.spectating || !!info.inGame;
    this.exitSpectating = !!info.spectating;
    this.specExitBtn.style.display = showExit ? '' : 'none';
    this.specExitBtn.textContent = `← ${t(this.exitSpectating ? 'hud.exitSpectate' : 'hud.exitGame')}`;
    // top-right gear/quick-settings menu disabled for now
    this.gearFab.style.display = 'none';
    this.quickPanel.style.display = 'none';
    // assessment/tutorial sessions each have their own UI in this corner; hide the
    // normal scoreboard rather than stacking cards behind it
    this.scorePanel.style.display = info.assessing || info.tutorial ? 'none' : '';
    this.cards.forEach((card, i) => {
      const p = state.players[i];
      if (!p || (solo && i === 1)) {
        card.style.display = 'none'; // no such team (or solo: only your card)
        return;
      }
      card.style.display = '';
      card.classList.toggle('active', i === state.activePlayerIndex && state.phase !== 'finished');
      const swatchColor = this.colorOverride[p.cueBall] ?? BALL_COLOR[p.cueBall];
      (card.querySelector('.swatch') as HTMLElement).style.background = '#' + swatchColor.toString(16).padStart(6, '0');
      // Team name: on a 2v2 team, render each partner and highlight the one whose
      // turn it is (by partnerTurn) so it's clear who's actually shooting.
      const nameEl = card.querySelector('.pname') as HTMLElement;
      const members = p.members ?? [p.name];
      if (members.length > 1) {
        const shooting = i === state.activePlayerIndex && state.phase !== 'finished' ? p.partnerTurn % members.length : -1;
        nameEl.replaceChildren();
        members.forEach((m, mi) => {
          if (mi > 0) nameEl.appendChild(document.createTextNode(' & '));
          const span = document.createElement('span');
          span.className = mi === shooting ? 'pname-member shooting' : 'pname-member';
          span.textContent = m;
          nameEl.appendChild(span);
        });
      } else {
        nameEl.textContent = p.name;
      }
      // handicap mode shows the countdown still to clear; otherwise the raw score.
      // Once cleared (0 left) show nothing — the final-stage chips take over.
      // A mission attempt's own handicap is an internal placeholder (ASSESS_HANDICAP)
      // with no meaning to the player — the pip badge above owns that story instead.
      const rem = info.daily ? 0 : pointsRemaining(p);
      (card.querySelector('.score') as HTMLElement).textContent = rem > 0 ? String(rem) : '';
      // unit only alongside a number (cleared players show the final-stage chips)
      (card.querySelector('.score-unit') as HTMLElement).textContent = rem > 0 ? t('score.left') : '';
      // final stage: one chip per remaining qualifying shot — plain 3쿠션 (orange),
      // the ones that must be banks shown as 가락 (teal), banks last.
      const onFinal = inFinalShotStage(state.config, state.config.rule, p) && state.phase !== 'finished';
      card.classList.remove('final-shot'); // final stage shows only the chips, no box highlight
      const badge = card.querySelector('.final-badge') as HTMLElement;
      badge.replaceChildren();
      if (onFinal) {
        const left = finalRemaining(state.config.rule, p);
        for (let k = 0; k < left.caroms; k++) {
          const isBank = k >= left.caroms - left.banks;
          const chip = document.createElement('span');
          chip.className = `final-chip ${isBank ? 'bank' : 'cushion'}`;
          chip.textContent = isBank ? t('score.finalBank') : t('score.finalCushion3');
          badge.appendChild(chip);
        }
      }
      // each player's all-time stats + my record against them, under their card
      const st = info.stats?.[i];
      this.renderCardStats(card, st, st ? info.h2h?.[st.id] : null);
    });

    // Korean 4-ball: once a player clears their handicap they enter the final
    // 3-cushion stage — flag it so the table knows a plain carom won't win.
    const finalist = state.players.findIndex((p) => inFinalShotStage(state.config, state.config.rule, p));
    if (info.daily) {
      // The player's config.solo target here is an internal assessment handicap
      // (see ASSESS_HANDICAP), not the mission's real goal — the pip badge above
      // carries the real progress, so the plain sub-line stays empty.
      this.renderDailyBadge(info.daily);
      this.sub.textContent = '';
    } else if (finalist >= 0 && state.phase !== 'finished') {
      const fp = state.players[finalist];
      const left = finalRemaining(state.config.rule, fp);
      this.sub.textContent = left.banks > 0
        ? t('status.finalCushionBanks', { name: fp.name, c: String(left.caroms), b: String(left.banks) })
        : t('status.finalCushion', { name: fp.name, c: String(left.caroms) });
    } else {
      this.sub.textContent = solo
        ? t('status.soloSub', { n: state.players[0].target })
        : state.players.length === 2
          ? t('status.handicapSub', { a: state.players[0].target, b: state.players[1].target })
          : t('status.targets', { list: state.players.map((p) => p.target).join(' · ') });
    }
    if (!info.daily) {
      this.dailyBadge.replaceChildren();
      this.dailyBadge.classList.remove('flash', 'shake');
      this.lastDailyCaroms = null;
      this.lastDailyStrokes = null;
    }
    this.message.textContent = info.message;

    // touch stroke pad: only accepts a pull-back when it's your turn to shoot
    this.strokeReady = info.canShoot;
    this.strokePad.classList.toggle('ready', info.canShoot);

    // Solo practice only: an AI or online opponent makes "replay my last shot"
    // either meaningless (nothing to review — the AI is scripted) or a way to
    // stall/scout a live opponent, so it's scoped to where it's just you and the
    // table. (Not "shown but disabled elsewhere" — hidden, so it doesn't read as
    // a feature that's broken outside solo.)
    this.replayBtn.style.display = info.mode === 'solo' ? '' : 'none';
    this.replayBtn.disabled = !info.canReplay;
    this.scrubRange.disabled = !info.canScrub;
    // online is its own button, not a dropdown option; only reflect local modes
    if (info.mode !== 'online') this.modeSel.value = info.mode;
    this.onlineBtn.classList.toggle('active', info.mode === 'online');
    this.guideOnState = info.guideOn;
    this.guideBtn.textContent = t(info.guideOn ? 'btn.guideOn' : 'btn.guideOff');
    this.cameraModeState = info.cameraMode;
    this.refreshCamToggleLabel();

    // the re-practice button follows the practice-mode flag, independent of the banner
    this.rePracticeBtn.style.display = info.showRePractice ? '' : 'none';

    // the end-of-game banner can be dismissed (e.g. when the review panel is opened);
    // reset that latch once there's no end banner to show (a new game / mid-play)
    const endBanner = !!info.assessResult || state.phase === 'finished';
    if (!endBanner) this.bannerDismissed = false;

    if (endBanner && this.bannerDismissed) {
      this.banner.classList.remove('show'); // dismissed → stay hidden (review is open)
      this.bannerMenuBtn.style.display = 'none';
      this.bannerReviewBtn.style.display = 'none';
    } else if (info.assessResult) {
      // assessment result takes priority (level + rank), with a play-again hint
      this.banner.classList.add('show');
      this.bannerText.innerHTML = info.assessResult + `<br><span style="font-size:13px;opacity:.8">${t('banner.again')}</span>`;
      this.bannerMenuBtn.style.display = ''; // offer a way back to the main menu
      this.bannerReviewBtn.style.display = info.canReview ? '' : 'none';
    } else if (state.phase === 'finished') {
      const again = `<br><span style="font-size:13px;opacity:.8">${t('banner.again')}</span>`;
      const players = state.players;
      let head: string;
      if (players.length >= 3 && players.some((p) => p.place != null)) {
        // 3+ player "play until last": show the final standings by finishing place
        const medal = (pl: number) => (pl === 1 ? '🥇' : pl === 2 ? '🥈' : pl === 3 ? '🥉' : `${pl}.`);
        const rows = [...players]
          .sort((a, b) => (a.place ?? 99) - (b.place ?? 99))
          .map((p) => `${medal(p.place ?? 99)} ${p.name}`)
          .join('<br>');
        head = `<b>${t('banner.standings')}</b><br>${rows}`;
      } else {
        const w = state.winnerIndex;
        head = w == null ? t('banner.draw') : solo ? t('banner.cleared') : t('banner.wins', { name: players[w].name });
      }
      this.banner.classList.add('show');
      this.bannerText.innerHTML = head + again;
      this.bannerMenuBtn.style.display = '';
      this.bannerReviewBtn.style.display = info.canReview ? '' : 'none';
    } else if (info.banner) {
      this.banner.classList.add('show');
      this.bannerText.textContent = info.banner;
      this.bannerText.classList.add('clickable'); // e.g. break's "press any key or click"
      this.bannerMenuBtn.style.display = 'none'; // mid-game prompt: no menu button
      this.bannerReviewBtn.style.display = 'none';
    } else {
      this.banner.classList.remove('show');
      this.bannerMenuBtn.style.display = 'none';
      this.bannerReviewBtn.style.display = 'none';
    }
    if (!info.banner) this.bannerText.classList.remove('clickable');
  }

  /** Hide the end-of-game banner (e.g. after the review panel is opened). It stays
   *  hidden until there's a fresh reason to show it (a new finished game). */
  dismissBanner(): void {
    this.bannerDismissed = true;
    this.banner.classList.remove('show');
  }

  setPower(power01: number): void {
    const p = Math.max(0, Math.min(1, power01));
    this.powerFill.style.width = (p * 100).toFixed(1) + '%';
  }

  /** Reflect a theme set elsewhere (e.g. synced from the online host) in the menu. */
  setTheme(id: ThemeId): void {
    this.themeSel.value = id;
  }

  /** Reflect a background scene set elsewhere (e.g. synced from the online host). */
  setEnv(id: EnvId): void {
    this.envSel.value = id;
    this.quickEnvSel.value = id;
  }

  /** Reflect the current target score / level (e.g. restored from last session). */
  setHandicap(value: number): void {
    this.handicapSel.value = String(value);
    this.quickHandicapSel.value = String(value);
  }

  /** Chalk level 0..1 on the tip; the bar reddens as it runs low (miscue risk). */
  setChalk(level01: number): void {
    const p = Math.max(0, Math.min(1, level01));
    this.chalkFill.style.width = (p * 100).toFixed(1) + '%';
    // green-blue when fresh → amber → red as the tip wears and miscues loom
    const hue = Math.round(p * 140); // 0 (red) .. 140 (green)
    this.chalkFill.style.background = `hsl(${hue}, 70%, 50%)`;
    // Touch: the button itself carries the level as a faint bottom-up fill
    // (see hud.css .chalk-btn) instead of the separate bar above it — there's
    // no room for a second gauge next to the camera/aim-boost buttons.
    const pct = (p * 100).toFixed(1) + '%';
    const faint = `hsl(${hue} 70% 50% / 0.35)`;
    this.chalkBtn.style.background = `linear-gradient(to top, ${faint} ${pct}, transparent ${pct}), var(--surface-raised)`;
  }

  /** The camera toggle always shows the mode a tap would switch TO (the next
   *  stop in CAM_CYCLE), so it reads as an action rather than a status. */
  private refreshCamToggleLabel(): void {
    const cur = CAM_CYCLE.indexOf(this.cameraModeState);
    const shown = cur >= 0 ? CAM_CYCLE[(cur + 1) % CAM_CYCLE.length] : CAM_CYCLE[0];
    setIconLabel(this.camToggleBtn, CAM_EMOJI[shown], t('cam.' + shown));
  }

  /** Cue tilt readout (degrees), shown in the controls pane. */
  setTilt(deg: number): void {
    this.tiltDeg = Math.round(deg);
    this.tiltReadout.textContent = t('ctrl.tilt', { d: this.tiltDeg });
    // Keep the dial in sync however the tilt was set (dial, mouse, or a reset
    // between shots) — a thumb that lies about the cue angle is worse than none.
    const frac = Math.max(0, Math.min(1, deg / MAX_TILT_DEG));
    this.tiltThumb.style.bottom = `${frac * 100}%`;
    this.tiltFill.style.height = `${frac * 100}%`;
    this.tiltDial.classList.toggle('raised', this.tiltDeg > 0);
  }
}
